/**
 * AppConfig Type Definitions
 * 
 * This configuration is injected by the backend when running on Google Cloud Run.
 * The window.AppConfig object contains all data needed to render the store selection page.
 */

import type { Store, SavedAddress, DeliveryAddress } from './index'

// ============================================================================
// RESTAURANT / BRAND TYPES
// ============================================================================

export interface RestaurantConfig {
  /** Unique identifier for this restaurant in your database */
  id: string
  /** Display name shown in header and SEO */
  name: string
  /** Tagline shown below the name */
  tagline: string
  /** Logo URL (optional - if not provided, name text is shown) */
  logoUrl: string
  /** Favicon URL */
  faviconUrl: string
  /** Contact information */
  contact: {
    email: string
    phone: string
    website: string
  }
}

// ============================================================================
// THEME / BRANDING TYPES
// ============================================================================

export interface ThemeColors {
  /** Primary brand color (used for buttons, highlights, header) */
  brandPrimary: string
  brandPrimaryHover: string
  brandPrimaryLight: string
  /** Secondary/accent color */
  brandSecondary: string
  /** Surface colors (backgrounds) */
  surfacePrimary: string
  surfaceSecondary: string
  surfaceTertiary: string
  surfaceElevated: string
  /** Text colors */
  textPrimary: string
  textSecondary: string
  textTertiary: string
  textInverse: string
  /** Border colors */
  borderLight: string
  borderDefault: string
  borderStrong: string
  /** Status colors */
  statusSuccess: string
  statusSuccessLight: string
  statusWarning: string
  statusWarningLight: string
  statusError: string
  statusErrorLight: string
  statusInfo: string
  statusInfoLight: string
}

export interface ThemeConfig extends ThemeColors {
  /** Dark mode overrides */
  dark?: Partial<ThemeColors>
}

// ============================================================================
// FEATURE FLAGS TYPES
// ============================================================================

export interface FeatureFlags {
  enablePickup: boolean
  enableDelivery: boolean
  enableTableBooking: boolean
  enableAboutPage: boolean
  enableDarkMode: boolean
  enableRatings: boolean
  enablePromotions: boolean
  enableFavorites: boolean
  enableSearch: boolean
  enableFilters: boolean
  enableMapView: boolean
  enableHours: boolean
  enableCallButton: boolean
}

// ============================================================================
// API CONFIGURATION TYPES
// ============================================================================

export interface ApiConfig {
  baseUrl: string
  cloudFunctionsUrl: string
  storageBaseUrl: string
  googleMapsApiKey: string
  mapTilerApiKey?: string
  mapTilerStyleUrl?: string
}

// ============================================================================
// SEO CONFIGURATION TYPES
// ============================================================================

export interface SeoConfig {
  title: string
  description: string
  keywords: string
  ogImage: string
}

// ============================================================================
// LOCALIZATION TYPES
// ============================================================================

export interface LocaleConfig {
  language: string
  country: string
  currency: string
  currencySymbol: string
  distanceUnit: 'km' | 'mi'
  timeFormat: '12h' | '24h'
}

// ============================================================================
// AUTHENTICATION STATE TYPES
// ============================================================================

export interface AuthUser {
  id: string
  email?: string
  displayName?: string
  photoUrl?: string
  phone?: string
}

export interface AuthConfig {
  isAuthenticated: boolean
  user: AuthUser | null
}

// ============================================================================
// MAIN APP CONFIG TYPE
// ============================================================================

export interface AppConfig {
  restaurant: RestaurantConfig
  theme: ThemeConfig
  stores: Store[]
  savedAddresses: SavedAddress[]
  recentAddresses: DeliveryAddress[]
  api: ApiConfig
  features: FeatureFlags
  seo: SeoConfig
  locale: LocaleConfig
  auth: AuthConfig
}

// ============================================================================
// WINDOW TYPE EXTENSION
// ============================================================================

declare global {
  interface Window {
    AppConfig: AppConfig
  }
}

// ============================================================================
// DEFAULT VALUES (used when AppConfig is not injected)
// ============================================================================

export const defaultFeatures: FeatureFlags = {
  enablePickup: true,
  enableDelivery: true,
  enableTableBooking: false,
  enableAboutPage: false,
  enableDarkMode: true,
  enableRatings: true,
  enablePromotions: true,
  enableFavorites: true,
  enableSearch: true,
  enableFilters: true,
  enableMapView: true,
  enableHours: true,
  enableCallButton: true,
}

export const defaultLocale: LocaleConfig = {
  language: 'en-AU',
  country: 'AU',
  currency: 'AUD',
  currencySymbol: '$',
  distanceUnit: 'km',
  timeFormat: '24h',
}

import { config } from '@/config/config'

export const defaultApi: ApiConfig = {
  baseUrl: config.api.baseUrl,
  cloudFunctionsUrl: config.api.cloudFunctionsUrl,
  storageBaseUrl: config.api.storageBaseUrl,
  googleMapsApiKey: '',
}
